/**
 * Utility for generating listing image URLs from listing IDs
 *
 * This module provides consistent image URL generation across the codebase,
 * matching the PHP backend pattern from WebApp/module/Antengo/src/Antengo/Impl/File.php
 */
import { isDevEnv, isStagingEnv } from '../data/js/server-constants';

const FILES_PER_DIR = 2000;

export type ImageSize = 'original' | 'thumb' | '160x160' | '320x320' | '640x640' | '1280x1280';
export type ImageFormat = 'webp' | 'avif' | 'jpg';
export type Environment = 'staging' | 'production' | 'auto';

export interface ImageUrlOptions {
  /** Image size variant (defaults to 'original') */
  size?: ImageSize;
  /** Photo index for multiple photos (defaults to 0) */
  photoIndex?: number;
  /** Image format (defaults to 'webp') */
  format?: ImageFormat;
  /** Environment override (defaults to 'auto' which detects from process.env) */
  env?: Environment;
  /** Cache-busting string appended as ?v= query param (e.g. updatedDate timestamp) */
  cacheBuster?: string;
}

/**
 * Generate subdirectory path for a listing ID
 * Matches the PHP implementation: WebApp/module/Antengo/src/Antengo/Impl/File.php::getSubdir
 *
 * @param id - Listing ID
 * @returns Subdirectory path (e.g., "1/500/123" for listing 3000123)
 *
 * @example
 * getSubdir(100) // "100"
 * getSubdir(3000) // "1/1000"
 * getSubdir(3000123) // "1/500/123"
 */
export function getSubdir(id: number): string {
  if (id < FILES_PER_DIR) {
    return id.toString();
  }

  const remainder = id % FILES_PER_DIR;
  return getSubdir(Math.floor(id / FILES_PER_DIR)) + '/' + remainder;
}

/**
 * Detect if we're in staging environment
 */
function isStagingEnvironment(): boolean {
  if (typeof process === 'undefined') return false;

  // The canonical IS_STG plus this module's own host signals: a client bundle built
  // for staging carries the API URL even where the Vercel vars are absent.
  return (
    isDevEnv() ||
    isStagingEnv() ||
    Boolean(process.env.NEXT_PUBLIC_API_URL?.includes('new.sellwild.com')) ||
    Boolean(process.env.NEXT_PUBLIC_API_URL?.includes('dev.sellwild.com'))
  );
}

/**
 * Get S3 bucket configuration based on environment
 */
function getS3Config(env: Environment): { bucket: string; region: string } {
  const isStaging = env === 'auto' ? isStagingEnvironment() : env === 'staging';

  return {
    bucket: isStaging ? 'antengo-listings-dev' : 'antengo-listings',
    region: isStaging ? 'us-west-1' : 'us-west-2'
  };
}

/**
 * Get size suffix for image filename
 */
function getSizeSuffix(size: ImageSize): string {
  switch (size) {
    case 'thumb':
      return '_t';
    case '160x160':
      return '_160x160';
    case '320x320':
      return '_320x320';
    case '640x640':
      return '_640x640';
    case '1280x1280':
      return '_1280x1280';
    case 'original':
    default:
      return '';
  }
}

/**
 * Generate a listing image URL from listing ID
 *
 * @param listingId - The listing ID
 * @param options - Configuration options for the URL
 * @returns Complete S3 URL for the listing image
 *
 * @example
 * // Basic usage
 * getListingImageUrl(12345)
 * // => "https://antengo-listings-dev.s3.us-west-1.amazonaws.com/supply_listings/6/345.webp"
 *
 * // With thumbnail size
 * getListingImageUrl(12345, { size: 'thumb' })
 * // => "https://antengo-listings-dev.s3.us-west-1.amazonaws.com/supply_listings/6/345_t.webp"
 *
 * // Second photo
 * getListingImageUrl(12345, { photoIndex: 1 })
 * // => "https://antengo-listings-dev.s3.us-west-1.amazonaws.com/supply_listings/6/345_1.webp"
 *
 * // Production environment with JPG format
 * getListingImageUrl(12345, { env: 'production', format: 'jpg' })
 * // => "https://antengo-listings.s3.us-west-2.amazonaws.com/supply_listings/6/345.jpg"
 */
export function getListingImageUrl(
  listingId: number,
  options: ImageUrlOptions = {}
): string {
  const {
    size = 'original',
    photoIndex = 0,
    format = 'webp',
    env = 'auto',
    cacheBuster
  } = options;

  const isStaging = env === 'auto' ? isStagingEnvironment() : env === 'staging';
  const { bucket, region } = getS3Config(env);
  const subdir = getSubdir(listingId);
  const sizeSuffix = getSizeSuffix(size);

  // Build filename: {subdir}[_{photoIndex}][_{sizeSuffix}].{format}
  let filename = subdir;

  // Add photo index if not the first photo
  // First photo: no suffix, second photo: _1, third photo: _2, etc.
  if (photoIndex > 0) {
    filename += `_${photoIndex}`;
  }

  // Add size suffix
  filename += sizeSuffix;

  // Add extension
  filename += `.${format}`;

  // Dev/Staging: Use S3 URLs directly
  // Production: Use CloudFront
  let url = isStaging
    ? `https://${bucket}.s3.${region}.amazonaws.com/supply_listings/${filename}`
    : `https://listings-static${(listingId % 4) + 1}.sellwild.com/supply_listings/${filename}`;

  if (cacheBuster) {
    url += `?v=${cacheBuster}`;
  }

  return url;
}

/**
 * Generate multiple image URLs for a listing (original + variants)
 *
 * @param listingId - The listing ID
 * @param photoIndex - Photo index (defaults to 0)
 * @param env - Environment override
 * @returns Object with different image size URLs
 *
 * @example
 * getListingImageUrls(12345)
 * // => {
 * //   url: "https://.../{subdir}.webp",
 * //   thumbUrl: "https://.../{subdir}_t.webp",
 * //   '320x320Url': "https://.../{subdir}_320x320.webp"
 * // }
 */
export function getListingImageUrls(
  listingId: number,
  photoIndex: number = 0,
  env: Environment = 'auto',
  cacheBuster?: string
): {
  url: string;
  thumbUrl: string;
  '160x160Url': string;
  '320x320Url': string;
  '640x640Url': string;
  '1280x1280Url': string;
} {
  return {
    // Use Lambda-generated sizes (no base .webp exists)
    url: getListingImageUrl(listingId, { photoIndex, env, size: '1280x1280', cacheBuster }),
    thumbUrl: getListingImageUrl(listingId, { photoIndex, env, size: '320x320', cacheBuster }),
    '160x160Url': getListingImageUrl(listingId, { photoIndex, env, size: '160x160', cacheBuster }),
    '320x320Url': getListingImageUrl(listingId, { photoIndex, env, size: '320x320', cacheBuster }),
    '640x640Url': getListingImageUrl(listingId, { photoIndex, env, size: '640x640', cacheBuster }),
    '1280x1280Url': getListingImageUrl(listingId, { photoIndex, env, size: '1280x1280', cacheBuster })
  };
}

/**
 * Legacy format - generates URLs in the format expected by existing code
 * Returns an array of photo objects with url, thumbUrl, and size variant URLs
 *
 * UPDATED: Lambda generates sized WebP variants from .jpg originals:
 * - Lambda creates: _1280x1280.webp, _640x640.webp, _320x320.webp, _160x160.webp, _80x80.webp
 * - Lambda does NOT create base .webp (without size suffix)
 * - Main url: Uses original .jpg (exists immediately after upload, no Lambda delay)
 * - thumbUrl: Uses _320x320.webp (Lambda's thumbnail size)
 * - For edit page: useOriginalUrl=true ensures photos show immediately after save/refresh
 */
export function getListingPhotos(
  listingId: number,
  photoCount: number = 1,
  env: Environment = 'auto',
  useOriginalUrl: boolean = false,
  cacheBuster?: string,
  photoOrder?: number[] | null
): Array<{ url: string; thumbUrl: string; '160x160Url': string; '320x320Url': string; '640x640Url': string; '1280x1280Url': string; originalUrl: string; photoIndex: number }> {
  const photos = [];

  const indices = photoOrder && photoOrder.length > 0 ? photoOrder : Array.from({ length: photoCount }, (_, idx) => idx);

  for (const i of indices) {
    // Original .jpg file (exists immediately after upload - no Lambda processing delay)
    const originalUrl = getListingImageUrl(listingId, { photoIndex: i, env, size: 'original', format: 'jpg', cacheBuster });

    // Main image: Use original .jpg if requested (for edit page), otherwise 1280x1280 webp
    const url = useOriginalUrl
      ? originalUrl
      : getListingImageUrl(listingId, { photoIndex: i, env, size: '1280x1280', cacheBuster });

    // Thumbnail: Use 320x320 variant (Lambda's thumbnail size)
    const thumbUrl = getListingImageUrl(listingId, { photoIndex: i, env, size: '320x320', cacheBuster });

    photos.push({
      url,
      thumbUrl,
      '160x160Url': getListingImageUrl(listingId, { photoIndex: i, env, size: '160x160', cacheBuster }),
      '320x320Url': getListingImageUrl(listingId, { photoIndex: i, env, size: '320x320', cacheBuster }),
      '640x640Url': getListingImageUrl(listingId, { photoIndex: i, env, size: '640x640', cacheBuster }),
      '1280x1280Url': getListingImageUrl(listingId, { photoIndex: i, env, size: '1280x1280', cacheBuster }),
      originalUrl,
      photoIndex: i
    });
  }

  return photos;
}

/**
 * Get user photo URLs
 * Generates URLs for user profile photos in S3
 *
 * @param userId - The user ID
 * @param hasPhoto - Whether user has a custom photo (from USERS.hasPhoto field)
 * @param updatedDate - Optional update date for cache busting
 * @param env - Environment override
 * @returns Object with url and thumbUrl
 *
 * @example
 * getUserPhotoUrls(12345, 1, new Date())
 * // => {
 * //   url: "https://antengo-users-dev.s3.us-west-1.amazonaws.com/users/6/345.jpg?date=1234567890",
 * //   thumbUrl: "https://antengo-users-dev.s3.us-west-1.amazonaws.com/users/6/345_t.jpg?date=1234567890"
 * // }
 */
export function getUserPhotoUrls(
  userId: number,
  hasPhoto: number = 0,
  updatedDate?: Date | null,
  env: Environment = 'auto'
): { url: string; thumbUrl: string } {
  // Return default photos if user doesn't have a custom photo
  if (hasPhoto !== 1) {
    return {
      url: 'https://users-static4.sellwild.com/users/default.jpg',
      thumbUrl: 'https://users-static4.sellwild.com/users/default_t.jpg'
    };
  }

  const isStaging = env === 'auto' ? isStagingEnvironment() : env === 'staging';
  const bucket = isStaging ? 'antengo-users-dev' : 'antengo-users';
  const region = 'us-west-1'; // User bucket is always in us-west-1

  const subdir = getSubdir(userId);
  const cacheParam = updatedDate ? `?date=${Math.floor(updatedDate.getTime() / 1000)}` : '';

  return {
    url: `https://${bucket}.s3.${region}.amazonaws.com/users/${subdir}.jpg${cacheParam}`,
    thumbUrl: `https://${bucket}.s3.${region}.amazonaws.com/users/${subdir}_t.jpg${cacheParam}`
  };
}
